import { useRecoilState } from "recoil";
import { useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, Modal, Row, Spin } from "antd";
import {
  ConditionalRenderer,
  HeightVariantTileWrapper,
  PgVisualizeWrapper,
} from "components";
import { ModalCloseIcon } from "icons";
import { getVideoRatio } from "utils/helpers";
import { videoFramesImages } from "store/atom";
import { base64ImageArrayForRest } from "./constants";
import { ImagesCarousel } from "components/preview-history-multi/images-carousel";
import { CriticalImages } from "components/preview-history-multi/critical-images";
import { ImagesActionHeader } from "components/preview-history-multi/images-action-header";
import { getVideoRatio1 } from "components/pg-visualize-wrapper/helpers";
import {
  getHighlightsTilesData,
  getLaunchTilesData,
  summaryDataConversionDTO,
} from "./helpers";
import { UseLazyApiCallPerformanceGolf } from "hooks";
import styles from "./styles.module.scss";

export function PgPreviewHistory({
  fileId,
  visible,
  videoSrc,
  extractionProgress,
  overallTimeLine,
  timeLineApiData,
  videoData,
  positionsData,
  positionsDataLoading,
}: any) {
  // const [activeValue, setActiveValue] = useState<string>("Pose");
  const [activeComponent, setActiveComponent] = useState<any>("pieChart");
  const [imageData, setImageData] = useState<any>(base64ImageArrayForRest);
  const containerHeightFix: any = "calc(100dvh - 340px)";

  const [heightOfContainer, setHeightOfContainer] =
    useState(containerHeightFix);
  const [widthOfContainer, setWidthOfContainer] = useState<any>();
  const [expandCriticalImages, setExpandCriticalImages] = useState(false);
  const [faceonAllImagesRecoil, setFaceonAllImagesRecoil] =
    useRecoilState(videoFramesImages);
  const [imagesCarousel, setImagesCarousel] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  const visualizeContainerRef = useRef<any>();

  const [
    getTracerImage,
    {
      data: tracerImageData,
      isLoading: tracerImageLoading,
      error: tracerImageError,
    },
  ] = UseLazyApiCallPerformanceGolf({
    url: "/api/data/pose-overlay",
    method: "POST",
    paramsData: {
      id: fileId,
      color: "red",
      include_data_url: true,
    },
  }) as any;

  const summaryData = summaryDataConversionDTO(videoData);

  useEffect(() => {
    getTracerImage();
  }, []);

  useEffect(() => {
    if (positionsData?.data?.images) {
      const imageData = convertUrlsToBase64({
        images: positionsData?.data?.images,
        data: summaryData,
      });

      setImageData(imageData);
    }
  }, [positionsData?.data?.images]);

  function urlToBase64(url: any) {
    return `data:image/jpeg;base64,${url}`;
  }

  function convertUrlsToBase64({ images, data }: any) {
    const base64ImageArray: any = [];
    let keysToCheck: any = [];

    if (data?.orientation === "Face On" && data?.swingType === "Full Swing") {
      keysToCheck = [
        "P1",
        "P2",
        "P3",
        "P4",
        "P5",
        "P6",
        "P7",
        "P8",
        "P9",
        "P10",
      ];
    } else if (
      (data?.orientation === "Face On" ||
        data?.orientation === "Down The Line") &&
      data?.swingType !== "Full Swing" &&
      data?.swingType !== "Scan"
    ) {
      keysToCheck = ["P1", "P4", "P7", "P10"];
    } else if (
      data?.orientation === "Down The Line" &&
      data?.swingType === "Full Swing"
    ) {
      keysToCheck = [
        "P1",
        "P2",
        "P3",
        "P4",
        "P5",
        "P6",
        "P7",
        "P8", //not required in case of dlt & fs
        "P9", //not required in case of dlt & fs
        "P10",
      ];
    } else if (data?.swingType === "Scan") {
      keysToCheck = [];
    } else {
      keysToCheck = ["P1", "P4", "P7", "P10"];
    }

    // Iterate over each key-value pair in the input data object

    keysToCheck.forEach((key: any) => {
      const url = images[key];
      if (url) {
        const base64Data = urlToBase64(url);
        base64ImageArray.push({ [key]: base64Data });
      } else {
        base64ImageArray.push({ [key]: null });
      }
    });

    return base64ImageArray;
  }

  const finalImages = useMemo(
    () =>
      imageData?.filter((image: any) =>
        Object.values(image)?.some((value) => value !== null)
      ),
    [imageData]
  );

  function onClickImage(imageName: any) {
    const index = finalImages?.findIndex(
      (item: any) => Object.keys(item)[0] === imageName
    );

    setExpandCriticalImages(true);
    setImagesCarousel(true);
    setImageIndex(index);
  }

  const tracerImage = tracerImageData?.data?.image
    ? urlToBase64(tracerImageData?.data?.image)
    : null;

  const containerHeight = visualizeContainerRef?.current?.clientHeight;
  const videoHeight = videoData?.resolution_height;
  const videoWidth = videoData?.resolution_width;

  const containerWidth: any = getVideoRatio(containerHeight);
  const containerWidthDynamic1 = getVideoRatio1({
    height: containerHeight,
    videoHeight,
    videoWidth,
  });

  const launchTilesData = getLaunchTilesData({}, {});
  const hightlightsTilesData = getHighlightsTilesData({});
  const pValueImages = positionsData?.data?.images
    ? {
        status: true,
        images: positionsData?.data?.images,
      }
    : { status: false, images: {} };
  const modifiedVideoData = {
    ...videoData,
    swingType: summaryData?.swingType,
    deviceType: summaryData?.deviceType || "IOS",
    orientation: summaryData?.orientation,
  };

  const isSwingTypeScan = summaryData?.swingType !== "Scan";

  return (
    <div style={{ width: "100%" }}>
      <Row>
        <Col
          span={9}
          xs={24}
          sm={24}
          md={24}
          lg={9}
          xxl={9}
          style={{ padding: "4px", marginBottom: "5px" }}
        >
          <Spin spinning={false}>
            <Card
              bordered={true}
              className={styles["serverCard"]}
              styles={{
                body: {
                  padding: "5px",
                  minHeight: "290px",
                  maxHeight: "290px",
                },
              }}
            >
              <div className={styles["cardTitleContent"]}>
                <div className={styles["iconContainer"]}>
                  <h4 className={styles["cardTitle"]}>Summary</h4>
                </div>
              </div>

              <div
                className={styles["cardMiddleSection"]}
                style={{
                  backgroundColor: "#EBF8ED",
                  marginTop: "1px",
                  minWidth: "200px",
                }}
              >
                <PropertyContainer1
                  imageSrc={"/assets/icons/uploadedBy.svg"}
                  propertyName={"Uploaded By:"}
                  propertyValue={summaryData?.uploadedBy || "-"}
                  marginRight
                />

                <PropertyContainer1
                  imageSrc={"/assets/icons/fileName.svg"}
                  propertyName={"Video Name:"}
                  propertyValue={summaryData?.videoName || ""}
                  marginRight
                />
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  marginTop: "5px",
                  justifyContent: "space-between",
                }}
              >
                <div
                  className={styles["cardMiddleSection"]}
                  style={{
                    backgroundColor: "rgba(0, 185, 244, 0.1)",
                    marginTop: "1px",
                    width: "47%",
                    height: "calc(270px - 70px)",
                    maxHeight: "200px",
                    overflow: "auto",
                  }}
                >
                  <PropertyContainer1
                    imageSrc={"/assets/icons/deviceType.svg"}
                    propertyName={"Device Type:"}
                    propertyValue={summaryData?.deviceType || "-"}
                  />
                  <PropertyContainer1
                    imageSrc={"/assets/icons/deviceModel.png"}
                    propertyName={"Device Model:"}
                    propertyValue={summaryData?.deviceModal || ""}
                  />

                  <ConditionalRenderer condition={isSwingTypeScan}>
                    <PropertyContainer1
                      imageSrc={"/assets/icons/camera.svg"}
                      propertyName={"Camera:"}
                      propertyValue={summaryData?.camera || ""}
                    />

                    {/* <PropertyContainer1
                      imageSrc={"/assets/icons/deviceAngle.svg"}
                      propertyName={"Device Angle:"}
                      propertyValue={summaryData?.deviceAngle || ""}
                    /> */}

                    <PropertyContainer1
                      imageSrc={"/assets/icons/iso.svg"}
                      propertyName={"ISO:"}
                      propertyValue={summaryData?.iso || "--"}
                    />

                    <PropertyContainer1
                      imageSrc={"/assets/icons/shutter.svg"}
                      propertyName={"Shutter:"}
                      propertyValue={summaryData?.shutter || "--"}
                    />
                  </ConditionalRenderer>

                  <PropertyContainer1
                    imageSrc={"/assets/icons/duration.svg"}
                    propertyName={"Duration:"}
                    propertyValue={summaryData?.duration || "--"}
                  />

                  <PropertyContainer1
                    imageSrc={"/assets/icons/fps.svg"}
                    propertyName={"Frame Rate:"}
                    propertyValue={summaryData?.frameRate || "--"}
                  />

                  <PropertyContainer1
                    imageSrc={"/assets/icons/resolution.svg"}
                    propertyName={"Resolution:"}
                    propertyValue={`${summaryData?.resolution}`}
                  />

                  {/* <PropertyContainer1
                    imageSrc={"/assets/icons/triggerType.svg"}
                    propertyName={"isCameraMoved:"}
                    propertyValue={videoData?.isCameraMoved || "--"}
                    // marginRight
                  /> */}
                </div>
                <div style={{ width: "50%" }}>
                  <div
                    className={styles["cardMiddleSection"]}
                    style={{
                      backgroundColor: "#F0F0F0",
                      marginTop: "1px",
                      height: "calc(270px - 70px)",
                      maxHeight: "200px",
                    }}
                  >
                    <PropertyContainer1
                      imageSrc={"/assets/icons/swingType.svg"}
                      propertyName={"Swing Type:"}
                      propertyValue={summaryData?.swingType || "--"}
                      marginRight
                    />

                    <PropertyContainer1
                      imageSrc={"/assets/icons/orientation.svg"}
                      propertyName={"Orientation:"}
                      propertyValue={summaryData?.orientation || "--"}
                      marginRight
                    />

                    <ConditionalRenderer condition={isSwingTypeScan}>
                      <PropertyContainer1
                        imageSrc={"/assets/icons/handedNess.svg"}
                        propertyName={"Handedness:"}
                        propertyValue={summaryData?.handedness || "--"}
                        marginRight
                      />

                      <PropertyContainer1
                        imageSrc={"/assets/icons/clubType.svg"}
                        propertyName={"Club Type:"}
                        propertyValue={summaryData?.clubType || "--"}
                        marginRight
                      />

                      <PropertyContainer1
                        imageSrc={"/assets/icons/triggerType.svg"}
                        propertyName={"Trigger Type:"}
                        propertyValue={summaryData?.triggerType || "--"}
                        marginRight
                      />
                    </ConditionalRenderer>
                  </div>
                </div>
              </div>
            </Card>
          </Spin>
        </Col>

        <ConditionalRenderer condition={false}>
          <Col
            span={4}
            sm={24}
            xs={24}
            md={4}
            lg={4}
            xxl={4}
            style={{ padding: "2px" }}
          >
            <Spin spinning={positionsDataLoading}>
              <Card
                className={styles.basicCardContainer}
                styles={{
                  body: {
                    padding: "5px",
                    minHeight: "262px",
                    maxHeight: "262px",
                  },
                }}
              >
                <h1 className={styles.basicCardHeading}>Highlights</h1>
                <div className={styles["basicCardData"]}>
                  {hightlightsTilesData?.map((item: any, index: any) => (
                    <HeightVariantTileWrapper
                      key={index}
                      title={item.title}
                      unit={item.unit}
                      value={item.value}
                      variant={item.variant}
                      clubFittingFirstColumn
                      isFirstColumn={index % 3 === 0 ? true : false}
                    />
                  ))}
                </div>
              </Card>
            </Spin>
          </Col>
        </ConditionalRenderer>

        <ConditionalRenderer condition={false}>
          <Col
            span={4}
            sm={24}
            xs={24}
            md={4}
            lg={4}
            xxl={4}
            style={{ padding: "2px" }}
          >
            <Spin spinning={positionsDataLoading}>
              <Card
                className={styles.basicCardContainer}
                styles={{
                  body: {
                    padding: "5px",
                    minHeight: "262px",
                    maxHeight: "262px",
                  },
                }}
              >
                <h1 className={styles.basicCardHeading}>Launch Values</h1>
                <div className={styles["basicCardData"]}>
                  {launchTilesData?.map((item: any, index: any) => (
                    <HeightVariantTileWrapper
                      key={index}
                      title={item.title}
                      unit={item.unit}
                      value={item.value}
                      variant={item.variant}
                      clubFittingFirstColumn
                    />
                  ))}
                </div>
              </Card>
            </Spin>
          </Col>
        </ConditionalRenderer>
      </Row>

      <div
        className={styles.videoAndImages}
        style={{
          display: "grid",
          gridTemplateColumns: `auto ${containerWidth} 2fr`,
          gap: "20px",
          alignItems: "center",
          marginTop: "5px",
        }}
      >
        <div
          style={{
            position: "relative",
            maxWidth: containerWidth,
            borderRadius: "6px",
            overflow: "hidden",
          }}
          id="video-container"
        >
          <PgVisualizeWrapper
            fileId={fileId}
            visible={visible}
            videoSrc={videoSrc}
            deviceType={videoData?.device_type}
            extractionProgress={extractionProgress}
            frames={faceonAllImagesRecoil}
            frameRate={videoData?.frame_rate || 120}
            totalFrames={positionsData?.data?.total_frames || 360}
          />
        </div>
        <Spin spinning={tracerImageLoading}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "black",
              // width: containerWidthDynamic1,
              maxWidth: containerWidth,
              width: widthOfContainer,
              height: heightOfContainer,
            }}
            className={styles.visualizeWrapperContainer}
            ref={visualizeContainerRef}
          >
            <img
              src={tracerImage || ""} // Access the base64 data from the object value
              width={containerWidthDynamic1}
              alt=""
              style={{
                // borderRadius: "5px",
                backgroundColor: "#f0f0f0", // Set background color or use backgroundImage: `url(${imageUrl})` for actual images
                height: `${containerHeight}px`,
              }}
            />
          </div>
        </Spin>
        <Spin spinning={false}>
          <Card
            bordered={true}
            styles={{
              body: {
                padding: "5px",
              },
            }}
          >
            <ImagesActionHeader
              expandCriticalImages={expandCriticalImages}
              isDesktop={false}
              setExpandCriticalImages={setExpandCriticalImages}
              setImageIndex={setImageIndex}
              setImagesCarousel={setImagesCarousel}
              showLayoutChangeIcon={false}
            />
            <CriticalImages
              PValueImages={pValueImages}
              swingType={modifiedVideoData?.swingType}
              videoHeight={videoWidth}
              videoWidth={videoHeight}
              ExpandImages={false}
              imageData={imageData}
              setExpandCriticalImages={setExpandCriticalImages}
              isDesktop={false}
              videoData={modifiedVideoData}
              loading={positionsDataLoading}
              onClickImage={onClickImage}
            />
          </Card>
        </Spin>
      </div>

      <Modal
        closeIcon={<ModalCloseIcon />}
        open={expandCriticalImages}
        onCancel={() => setExpandCriticalImages(false)}
        footer={null}
        style={{
          width: "97vw", // Set width to 100vw
          maxWidth: "100%", // Ensure the modal doesn't overflow
          // height: "95dvh",
          // top: 5,
        }}
        centered
        styles={{
          body: {
            height: "98dvh",
          },
          content: {
            padding: "8px 20px",
          },
        }}
        width="97VW"
        destroyOnClose={true}
      >
        <ImagesActionHeader
          expandCriticalImages={expandCriticalImages}
          isDesktop={false}
          setExpandCriticalImages={setExpandCriticalImages}
          setImageIndex={setImageIndex}
          setImagesCarousel={setImagesCarousel}
          showLayoutChangeIcon
        />
        <div style={{ height: "94dvh" }}>
          <ConditionalRenderer condition={!imagesCarousel}>
            <CriticalImages
              PValueImages={pValueImages}
              swingType={modifiedVideoData?.swingType}
              videoHeight={videoWidth}
              videoWidth={videoHeight}
              imageData={imageData}
              setExpandCriticalImages={setExpandCriticalImages}
              ExpandImages
              isDesktop={false}
              videoData={modifiedVideoData}
              loading={positionsDataLoading}
              onClickImage={onClickImage}
            />
          </ConditionalRenderer>
          <ConditionalRenderer condition={imagesCarousel}>
            <ImagesCarousel
              videoHeight={videoWidth}
              videoWidth={videoHeight}
              imageData={imageData}
              imageIndex={imageIndex}
              positions={positionsData?.data?.positions || {}}
            />
          </ConditionalRenderer>
        </div>
      </Modal>
    </div>
  );
}

function PropertyContainer1({
  imageSrc,
  propertyName,
  propertyValue,
  marginRight = false,
}: any) {
  return (
    <div className={styles["entry"]}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <div style={{ verticalAlign: "middle", display: "flex" }}>
          <img
            src={`${imageSrc}`}
            alt={`${propertyName}-icon`}
            className={styles["entryIcon"]}
          />
        </div>
        <div
          style={{ marginRight: marginRight ? "10px" : "0px" }}
          className={styles.propertyName}
        >
          <h4 className={styles["entrySubheading"]}>{propertyName}</h4>
        </div>
      </div>
      <div>
        <span className={styles["entryValue"]}>{propertyValue || "--"}</span>
      </div>
    </div>
  );
}
